import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, Plus, Minus, ShoppingBag, ArrowLeft, Tag, CalendarHeart } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { fmtMoney } from "@/components/status";
import ImageCarousel from "@/components/ImageCarousel";
import { useLightbox } from "@/components/Lightbox";

function ProductCard({ product, qty, step, onAdd, isEvent, closed }) {
  const openLightbox = useLightbox();
  const soldOut = (product.is_special && (product.stock || 0) <= 0) || closed;
  return (
    <div data-testid={`flower-card-${product.id}`} className={`flex items-center gap-3 rounded-2xl bg-white/10 p-3 ${soldOut ? "opacity-50" : ""}`}>
      <ImageCarousel testid={`flower-card-images-${product.id}`} images={product.images && product.images.length ? product.images : [product.image_url]} alt={product.name} className="h-16 w-16" onOpen={openLightbox} />
      <div className="min-w-0 flex-1">
        <div className="truncate font-semibold">{product.name}</div>
        {(product.images || []).length > 1 && <div data-testid={`flower-card-swipe-hint-${product.id}`} className="text-[10px] font-semibold uppercase tracking-wide text-[#EBBAD1]/90">Swipe for {product.images.length} photos</div>}
        <div className="text-xs font-medium text-white/85">{isEvent ? `OM ${product.order_multiple || product.pack_size}` : `1 carton • ${product.pack_size} ${product.pack_unit || "units"}`}</div>
        <div className="text-sm font-bold text-[#EBBAD1]">
          {product.show_dc_cost === false ? null : product.price > 0 ? <>{fmtMoney(product.price)} <span className="text-xs font-normal text-white/82">/ {product.unit || "carton"}</span></> : <span data-testid={`flower-card-price-tbc-${product.id}`}>Price TBC</span>}
          {product.show_sell_price !== false && product.sell_price > 0 ? <span className="text-xs font-normal text-white/82">{product.show_dc_cost === false ? "" : " · "}sell {fmtMoney(product.sell_price)}</span> : null}
        </div>
      </div>
      {soldOut ? (
        <span className="rounded-full bg-black/30 px-3 py-1 text-xs font-bold text-white/85">{closed ? "Ordering closed" : "Sold out"}</span>
      ) : qty === 0 ? (
        <button data-testid={`flower-card-add-button-${product.id}`} onClick={() => onAdd(step)} className="flex items-center gap-1 rounded-full bg-[#28282E] px-4 py-2 text-sm font-bold text-white hover:bg-[#1F1F24]"><Plus className="h-4 w-4" /> Add</button>
      ) : (
        <div className="flex items-center gap-2 rounded-full bg-black/20 p-1">
          <button data-testid={`flower-card-minus-${product.id}`} onClick={() => onAdd(-step)} className="grid h-8 w-8 place-items-center rounded-full bg-[#EBBAD1] text-[#28282E]"><Minus className="h-4 w-4" /></button>
          <span data-testid={`flower-card-qty-${product.id}`} className="min-w-[1.5rem] text-center font-bold">{qty}</span>
          <button data-testid={`flower-card-plus-${product.id}`} onClick={() => onAdd(step)} className="grid h-8 w-8 place-items-center rounded-full bg-[#EBBAD1] text-[#28282E]"><Plus className="h-4 w-4" /></button>
        </div>
      )}
    </div>
  );
}

export default function Catalogue() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const view = params.get("view"); // 'specials' | 'events' | null
  const { items, add, count, total } = useCart();
  const [flowers, setFlowers] = useState([]);
  const [specials, setSpecials] = useState([]);
  const [events, setEvents] = useState([]);
  const [activeEvent, setActiveEvent] = useState("");
  const [eventFlowers, setEventFlowers] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");

  useEffect(() => {
    api.events().then(setEvents).catch(() => {});
    if (view === "specials") api.specials().then(setSpecials).catch(() => {});
    else if (view !== "events") api.catalogue().then(setFlowers).catch(() => {});
  }, [view]);

  useEffect(() => {
    if (view === "events" && events.length && !events.some((e) => e.key === activeEvent)) setActiveEvent(params.get("event") || events[0].key);
  }, [view, events, activeEvent, params]);

  // Admin setting: a live event with display_mode 'instead' replaces the regular catalogue
  const replacing = !view && events.find((e) => e.display_mode === "instead");
  useEffect(() => { if (replacing && !sessionStorage.getItem("event_redirected")) { sessionStorage.setItem("event_redirected", "1"); navigate(`/store/catalogue?view=events&event=${replacing.key}`, { replace: true }); } }, [replacing, navigate]);
  const currentEvent = view === "events" ? events.find((e) => e.key === activeEvent) : null;

  useEffect(() => {
    if (view === "events" && activeEvent) api.eventFlowers(activeEvent).then(setEventFlowers).catch(() => {});
  }, [view, activeEvent]);

  const categories = useMemo(() => ["All", ...Array.from(new Set(flowers.map((f) => f.category).filter(Boolean)))], [flowers]);
  const step = () => 1; // orders are in cartons — increment 1 carton at a time

  let baseList = flowers;
  if (view === "specials") baseList = specials.map((s) => ({ ...s, is_special: true, special_id: s.id, order_multiple: 1 }));
  else if (view === "events") baseList = eventFlowers;

  const filtered = baseList.filter((f) => {
    const matchSearch = f.name.toLowerCase().includes(search.toLowerCase());
    const matchCat = view || category === "All" || f.category === category;
    return matchSearch && matchCat;
  });

  const title = view === "specials" ? "Friday Specials" : view === "events" ? (currentEvent ? `${currentEvent.name} Catalogue` : "Events") : "Flower Catalogue";
  const Icon = view === "specials" ? Tag : view === "events" ? CalendarHeart : null;

  const ReviewOrderButton = ({ testid }) => (
    <button data-testid={testid} onClick={() => navigate("/store/cart")}
      className="flex w-full items-center justify-between gap-3 rounded-2xl bg-[#28282E] px-4 py-3 font-semibold text-white transition hover:bg-[#1F1F24]">
      <span className="flex items-center gap-2"><ShoppingBag className="h-5 w-5 text-[#EBBAD1]" /> Review Order</span>
      <span className="rounded-full bg-white/15 px-3 py-1 text-sm font-bold">{count} {count === 1 ? "item" : "items"} · {fmtMoney(total)}</span>
    </button>
  );

  return (
    <div className="animate-fade-up" data-testid={`catalogue-view-${view || "all"}`}>
      {view && (
        <button onClick={() => navigate("/store")} data-testid="catalogue-back-home" className="mb-3 flex items-center gap-1 text-sm text-[#EBBAD1]"><ArrowLeft className="h-4 w-4" /> Back to home</button>
      )}
      <h1 className="mb-3 flex items-center gap-2 font-serif text-2xl font-extrabold">{Icon && <Icon className="h-6 w-6 text-[#EBBAD1]" />}{title}</h1>

      <div className="mb-4"><ReviewOrderButton testid="catalogue-review-top" /></div>

      {view === "specials" && <p className="mb-3 text-sm text-white/85">Warehouse extras with limited stock — order before they sell out.</p>}

      {!view && events.length > 0 && (
        <div data-testid="event-live-banner" className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-2xl bg-[#EBBAD1] px-4 py-3 text-[#28282E]">
          <span className="flex items-center gap-2 text-sm font-bold"><CalendarHeart className="h-4 w-4" /> {events.map((e) => e.name).join(" & ")} ordering is open</span>
          <button data-testid="event-live-banner-link" onClick={() => navigate(`/store/catalogue?view=events&event=${events[0].key}`)} className="rounded-full bg-[#28282E] px-3 py-1 text-xs font-bold text-white">Order for {events[0].name}</button>
        </div>
      )}

      {view === "events" && currentEvent && (
        <div data-testid="event-ordering-banner" className="mb-4 rounded-2xl bg-[#EBBAD1] px-4 py-3 text-[#28282E]">
          <div className="text-sm font-extrabold uppercase tracking-wide">You are ordering for {currentEvent.name}</div>
          <div className="text-xs font-medium">{currentEvent.description || "Items in this catalogue are for the event only."}{currentEvent.start_date ? ` · ${currentEvent.start_date} → ${currentEvent.end_date || "…"}` : ""} · {currentEvent.order_cutoff ? <span data-testid="event-cutoff">{currentEvent.ordering_open === false ? "Ordering closed" : `Order by ${currentEvent.order_cutoff}`}</span> : null}{currentEvent.delivery_days?.length ? ` · Delivery ${currentEvent.delivery_days.map((d) => d.toUpperCase()).join("/")}` : ""} · <button data-testid="event-back-to-regular" onClick={() => navigate("/store/catalogue")} className="underline">Back to regular catalogue</button></div>
        </div>
      )}

      {view === "events" && events.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2" data-testid="event-chips">
          <button data-testid="event-chip-regular" onClick={() => navigate("/store/catalogue")} className="rounded-full bg-white/10 px-3.5 py-1.5 text-xs font-bold text-white/90 transition hover:bg-white/20">Regular</button>
          {events.map((e) => (
            <button key={e.key} data-testid={`event-chip-${e.key}`} onClick={() => setActiveEvent(e.key)}
              className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition ${activeEvent === e.key ? "bg-[#EBBAD1] text-[#28282E]" : "bg-white/10 text-white/90 hover:bg-white/20"}`}>{e.name}</button>
          ))}
        </div>
      )}

      <div className="relative mb-3">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/78" />
        <input data-testid="catalogue-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search flowers"
          className="w-full rounded-full border border-white/20 bg-black/15 py-2.5 pl-9 pr-4 text-sm text-white placeholder:text-white/78 outline-none focus:border-[#EBBAD1]" />
      </div>

      {!view && (
        <div className="mb-4 flex flex-wrap gap-2">
          {categories.map((c) => (
            <button key={c} data-testid={`catalogue-category-${c.toLowerCase().replace(/\s+/g, "-")}-tab`} onClick={() => setCategory(c)}
              className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition ${category === c ? "bg-[#EBBAD1] text-[#28282E]" : "bg-white/10 text-white/90 hover:bg-white/20"}`}>{c}</button>
          ))}
          <button data-testid="catalogue-category-events-tab" onClick={() => navigate(`/store/catalogue?view=events${events[0] ? `&event=${events[0].key}` : ""}`)}
            className="flex items-center gap-1 rounded-full bg-[#28282E] px-3.5 py-1.5 text-xs font-bold text-white transition hover:bg-[#1F1F24]">
            <CalendarHeart className="h-3.5 w-3.5" /> Events
          </button>
        </div>
      )}

      <div className="space-y-3">
        {filtered.map((f) => (
          <ProductCard key={f.id} product={f} qty={items[f.id]?.qty || 0} step={step(f)} onAdd={(m) => add(f, m)} isEvent={view === "events"} closed={view === "events" && currentEvent && currentEvent.ordering_open === false} />
        ))}
        {filtered.length === 0 && (
          <p className="py-16 text-center text-sm text-white/82">
            {view === "events" && !events.length ? "No events available right now." : view === "specials" && !specials.length ? "No specials this week — check back soon." : "Nothing matches your search."}
          </p>
        )}
      </div>

      {filtered.length > 0 && (
        <div className="mt-6"><ReviewOrderButton testid="catalogue-review-bottom" /></div>
      )}
    </div>
  );
}
